import { InfoCircledIcon } from '@radix-ui/react-icons'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '#app/components/ui/tooltip'
import { cn } from '#app/utils/misc'

const badgeVariants = cva(
	'inline-flex cursor-default items-center leading-none rounded-full border font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
	{
		variants: {
			variant: {
				default: 'border-transparent bg-primary text-primary-foreground',
				secondary: 'border-transparent bg-secondary text-secondary-foreground',
				destructive:
					'border-transparent bg-destructive text-destructive-foreground',
				outline: 'text-foreground',
			},
			interactive: {
				true: '',
			},
			size: {
				'1': 'px-1.5 h-4 text-[0.65rem] gap-1',
				'2': 'px-3 py-2 text-xs gap-1',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: '1',
		},
		compoundVariants: [
			{
				variant: 'default',
				interactive: true,
				class: 'hover:bg-primary/80',
			},
			{
				variant: 'secondary',
				interactive: true,
				class: 'hover:bg-secondary/80',
			},

			{
				variant: 'destructive',
				interactive: true,
				class: 'hover:bg-destructive/80',
			},
		],
	},
)

interface WithTooltipProps {
	tooltip?: React.ReactNode
	tooltipDelayDuration?: number
}

export interface BadgeProps
	extends React.HTMLAttributes<HTMLDivElement>,
		VariantProps<typeof badgeVariants>,
		WithTooltipProps {
	leftIcon?: React.ReactNode
	rightIcon?: React.ReactNode
}

function Badge({
	className,
	variant,
	tooltip,
	interactive = !!tooltip,
	size,
	children,
	leftIcon: _leftIcon,
	rightIcon: _rightIcon,
	tooltipDelayDuration,
	...rest
}: BadgeProps) {
	let leftIcon = _leftIcon
	let rightIcon = _rightIcon

	if (tooltip && !leftIcon) {
		leftIcon = <InfoCircledIcon className="size-3.5" />
	}

	const content = (
		<div
			className={cn(badgeVariants({ variant, interactive, size }), className)}
			{...rest}
		>
			{leftIcon}
			{children}
			{rightIcon}
		</div>
	)

	if (tooltip) {
		return (
			<Tooltip delayDuration={tooltipDelayDuration}>
				<TooltipTrigger asChild>{content}</TooltipTrigger>
				<TooltipContent>{tooltip}</TooltipContent>
			</Tooltip>
		)
	}

	return content
}

export { Badge, badgeVariants }
